import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Route, Routes, useLocation } from 'react-router';
import { routes } from 'config';
import browserUpdate from 'browser-update';
import { AccountContext } from 'context';
import { ContentLoader } from 'modules/SharedComponents/Toolbox/Loaders';
import * as pages from 'modules/App/components/pages';
import Grid from '@mui/material/Grid';
import { getHomepageLink } from 'modules/Pages/LearningResources/shared/learningResourcesHelpers';

browserUpdate({
    required: {
        e: -2,
        i: 11,
        f: -2,
        o: -2,
        s: -1,
        c: -2,
        samsung: 7.0,
        vivaldi: 1.2,
    },
    insecure: true,
    style: 'top',
    shift_page_down: true,
});

const isAdminPage = () => {
    return window.location.pathname.startsWith('/admin/') || window.location.hash.startsWith('#/admin/');
};

const srOnlyAnnouncementStyle = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
};

// Announcement code created for spaces, with support for other homepage react components.
// at time of THIS deployment, spaces is not yet fully integrated into the homepage react.
// therefore, this is IGNORED in coverage - for now.
// ticket to fully integrate spaces into the homepage react is TBD.

/* istanbul ignore next */
const getPageAnnouncement = ({ pathname = '', hash = '' } = {}) => {
    const currentPath = `${pathname}${hash}`;

    let spokenContent = '';

    if (currentPath === '/spaces' || currentPath === '/spaces/' || currentPath === '/#/spaces') {
        spokenContent = 'Bookable Spaces.';
    }

    if (currentPath.startsWith('/spaces/results') || currentPath.includes('#/spaces/results')) {
        spokenContent = 'Bookable Spaces search results.';
    }

    if (currentPath.startsWith('/spaces/mapresults') || currentPath.includes('#/spaces/mapresults')) {
        spokenContent = 'Bookable Spaces map.';
    }

    if (currentPath.startsWith('/spaces/details/') || currentPath.startsWith('/spaces/detail/')) {
        spokenContent = 'Space Details.';
    }

    if (currentPath.includes('#/spaces/details/') || currentPath.includes('#/spaces/detail/')) {
        spokenContent = 'Space Details.';
    }
    // Announcements: Art trail
    if (
        currentPath.startsWith('/art-trail') ||
        currentPath.startsWith('/art-trail/') ||
        currentPath.includes('#/art-trail')
    ) {
        spokenContent = 'Indigenous Art and Discovery Trail.';
    }

    return `${spokenContent} Library. The University of Queensland.`;
};

const isStandaloneRoute = (pathname, routesConfig) => {
    return routesConfig.some(route => route.standalone && pathname.includes(route.path));
};

export const App = ({ account, actions }) => {
    const location = useLocation();
    const [liveAnnouncement, setLiveAnnouncement] = useState('Library');

    useEffect(() => {
        actions.loadCurrentAccount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setLiveAnnouncement(getPageAnnouncement(location));
    }, [location]);

    const routesConfig = routes.getRoutesConfig({
        components: pages,
        account: account,
    });

    // The routed pages, with the account made available to them. Rendered either on their own (standalone
    // sections) or inside the shared Library chrome below.
    const routedContent = (
        <AccountContext.Provider
            value={{
                account: {
                    ...account,
                },
            }}
        >
            <React.Suspense fallback={<ContentLoader message="Loading" />}>
                <Routes>
                    {routesConfig.map((route, index) => (
                        <Route key={`route_${index}`} {...route} />
                    ))}
                </Routes>
            </React.Suspense>
        </AccountContext.Provider>
    );

    // A standalone section brings its own look and feel, so it is rendered without the shared chrome.
    if (isStandaloneRoute(location.pathname, routesConfig)) {
        return <div data-testid="standalone-layout">{routedContent}</div>;
    }

    const homepagelink = getHomepageLink();
    let homepageLabel = 'Library';
    /* istanbul ignore next */
    if (window.location.hostname === 'homepage-development.library.uq.edu.au') {
        homepageLabel = 'Library Dev';
    } else if (window.location.hostname === 'homepage-staging.library.uq.edu.au') {
        homepageLabel = 'Library Staging';
    } else if (window.location.hostname === 'localhost') {
        homepageLabel = 'Library Local';
    }

    return (
        <Grid
            container
            sx={{
                position: 'relative',
                display: 'flex',
                flexFlow: 'column',
                margin: 0,
                padding: 0,
                maxHeight: '100%',
                height: '100%',
            }}
        >
            <div className="content-container" id="content-container" role="region" aria-label="Site content">
                <div aria-live="polite" aria-atomic="true" style={srOnlyAnnouncementStyle}>
                    {liveAnnouncement}
                </div>
                <uq-header hidelibrarymenuitem="true" skipnavid="content" />
                <uq-site-header sitetitle={homepageLabel} siteurl={homepagelink}>
                    <span slot="site-utilities">
                        <auth-button />
                    </span>
                </uq-site-header>
                {!isAdminPage() && <proactive-chat />}
                <div role="region" aria-label="UQ Library Alerts">
                    <alert-list system="homepage" />
                </div>
                <cultural-advice />
                <div style={{ flexGrow: 1 }}>
                    <main id="content" tabIndex="-1">
                        {routedContent}
                    </main>
                </div>
                <div id="full-footer-block" style={{ marginTop: '50px' }}>
                    <uq-footer />
                </div>
            </div>
        </Grid>
    );
};

App.propTypes = {
    account: PropTypes.object,
    actions: PropTypes.any,
    chatStatus: PropTypes.object,
    isSessionExpired: PropTypes.any,
};

export default App;
